import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import {
	ApplicantReference,
	Application,
	BooleanValue,
	Feedback,
	FunctionaryFeedbackRequest,
	UserRole,
	Preceptor,
	UpdatePreceptorReferenceRequest,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	searchPreceptorByAbhyasiId,
	submitPreceptorInterviewFeedback,
	updatePreceptorReference,
} from '../../../service/VolunteerApplicationService';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useAuth } from '../../../hooks/useAuth';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useInterviewWizard } from '../useInterviewWizard';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReviewAction } from '../../../shared/Constants';
import {
	addMandatoryQuestionsToFunctionaryFeedback,
	transformToAnswersForMandatoryQuestionsFromGRPC,
} from '../../../shared/Utils';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { ExclamationCircleFilled } from '@ant-design/icons';
import useUserSession from '../../../hooks/useUserSession';

export interface PreceptorInterviewContextType {
	inspirationAndWillingness: Feedback;
	setInspirationAndWillingness: (feedback: Feedback) => void;
	practice: Feedback;
	setPractice: (feedback: Feedback) => void;
	characterAndHabits: Feedback;
	setCharacterAndHabits: (feedback: Feedback) => void;
	availabilityAfterProgram: Feedback;
	setAvailabilityAfterProgram: (feedback: Feedback) => void;
	support: Feedback;
	setSupport: (feedback: Feedback) => void;
	understandingOfHeartfulness: Feedback;
	setUnderstandingOfHeartfulness: (feedback: Feedback) => void;
	dietaryHabitsAndHealth: Feedback;
	setDietaryHabitsAndHealth: (feedback: Feedback) => void;
	application: Partial<Application>;
	preceptorRecommendation: Feedback;
	setPreceptorRecommendation: (feedback: Feedback) => void;
	preceptorReference: ApplicantReference;
	setPreceptorReference: (reference: ApplicantReference) => void;
	onSubmitClick: () => void;
	onCancelClick: () => void;
	fetchPreceptorByAbhyasiId: (
		abhyasiId: string
	) => Promise<Preceptor | undefined>;
	updatePreceptorDetails: (
		preceptorDetails: ApplicantReference
	) => Promise<BooleanValue | undefined>;
}

interface Props {
	children: ReactNode;
}

const PreceptorInterviewContext = createContext<PreceptorInterviewContextType>({
	inspirationAndWillingness: {},
	setInspirationAndWillingness: () => {},
	practice: {},
	setPractice: () => {},
	characterAndHabits: {},
	setCharacterAndHabits: () => {},
	availabilityAfterProgram: {},
	setAvailabilityAfterProgram: () => {},
	support: {},
	setSupport: () => {},
	understandingOfHeartfulness: {},
	setUnderstandingOfHeartfulness: () => {},
	dietaryHabitsAndHealth: {},
	setDietaryHabitsAndHealth: () => {},
	preceptorRecommendation: {},
	setPreceptorRecommendation: () => {},
	application: {},
	preceptorReference: {},
	setPreceptorReference: () => {},
	onSubmitClick: () => {},
	onCancelClick: () => {},
	fetchPreceptorByAbhyasiId: () => Promise.resolve(undefined),
	updatePreceptorDetails: () => Promise.resolve(undefined),
});

export const PreceptorInterviewProvider: FC<Props> = ({ children }: Props) => {
	const { i18n, t } = useTranslation();
	const { srcmId } = useAuth();
	const [inspirationAndWillingness, setInspirationAndWillingness] =
		useState<Feedback>({});
	const [practice, setPractice] = useState<Feedback>({});
	const [characterAndHabits, setCharacterAndHabits] = useState<Feedback>({});
	const [availabilityAfterProgram, setAvailabilityAfterProgram] =
		useState<Feedback>({});
	const [support, setSupport] = useState<Feedback>({});
	const [understandingOfHeartfulness, setUnderstandingOfHeartfulness] =
		useState<Feedback>({});
	const [dietaryHabitsAndHealth, setDietaryHabitsAndHealth] =
		useState<Feedback>({});
	const [preceptorRecommendation, setPreceptorRecommendation] =
		useState<Feedback>({});
	const [application, setApplication] = useState<Partial<Application>>({});
	const [preceptorReference, setPreceptorReference] = useState<
		Partial<ApplicantReference>
	>({});
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { confirmAndSave, onCancelClick, callApi } = useInterviewWizard();
	const { selectedRole } = useUserSession();
	const { infoPopUpAction } = useInfoPopup();

	const saveFeedback = useCallback(async () => {
		const feedback: FunctionaryFeedbackRequest = {
			applicationId,
			categoryReviewFeedback: {
				availabilityAfterProgram,
				characterAndHabits,
				inspirationAndWillingness,
				practice,
				support,
				understandingOfHeartfulness,
				dietaryHabitsAndHealth,
			},
			feedbackCategory: 'PRECEPTOR_INTERVIEW',
			functionarySrcmId: srcmId as string,
			selectedRoleCode: selectedRole as UserRole,
		};

		const feedbackWithMandatoryQuestionsAndAnswers =
			addMandatoryQuestionsToFunctionaryFeedback(feedback, i18n.language);

		const request = {
			feedback: feedbackWithMandatoryQuestionsAndAnswers,
			recommendation: preceptorRecommendation,
			preceptorReference,
			selectedRoleCode: selectedRole as UserRole,
		};

		if (preceptorRecommendation.review === ReviewAction.APPROVE) {
			request.recommendation.predefinedReasons = [];
		}

		try {
			await callApi(
				() => submitPreceptorInterviewFeedback(request),
				'errorOccurredWhileSavingFeedback'
			);
			infoPopUpAction(
				'savedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(INTERVIEW_LANDING);
				},
				ConfirmTick
			);
		} catch (e) {
			/* empty */
		}
	}, [
		applicationId,
		availabilityAfterProgram,
		characterAndHabits,
		inspirationAndWillingness,
		practice,
		support,
		understandingOfHeartfulness,
		dietaryHabitsAndHealth,
		srcmId,
		selectedRole,
		i18n.language,
		preceptorRecommendation,
		preceptorReference,
		callApi,
		infoPopUpAction,
		navigateWithApplicationId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmAndSave(saveFeedback);
	}, [confirmAndSave, saveFeedback]);

	const fetchApplication = useCallback(async () => {
		try {
			const fetchedApplication = await callFetchApplication();

			const preceptorInterviewFeedbackWithAnswersForMandatoryQuestions =
				transformToAnswersForMandatoryQuestionsFromGRPC(
					fetchedApplication.preceptorInterviewFeedback
				);

			if (
				!inspirationAndWillingness ||
				Object.keys(inspirationAndWillingness).length === 0
			) {
				setInspirationAndWillingness(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.inspirationAndWillingness ??
						{}
				);
			}

			if (!practice || Object.keys(practice).length === 0) {
				setPractice(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.practice ??
						{}
				);
			}

			if (
				!availabilityAfterProgram ||
				Object.keys(availabilityAfterProgram).length === 0
			) {
				setAvailabilityAfterProgram(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.availabilityAfterProgram ??
						{}
				);
			}

			if (!characterAndHabits || Object.keys(characterAndHabits).length === 0) {
				setCharacterAndHabits(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.characterAndHabits ??
						{}
				);
			}

			if (!support || Object.keys(support).length === 0) {
				setSupport(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.support ??
						{}
				);
			}

			if (
				!understandingOfHeartfulness ||
				Object.keys(understandingOfHeartfulness).length === 0
			) {
				setUnderstandingOfHeartfulness(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.understandingOfHeartfulness ??
						{}
				);
			}

			if (
				!dietaryHabitsAndHealth ||
				Object.keys(dietaryHabitsAndHealth).length === 0
			) {
				setDietaryHabitsAndHealth(
					preceptorInterviewFeedbackWithAnswersForMandatoryQuestions?.dietaryHabitsAndHealth ??
						{}
				);
			}

			if (
				!preceptorRecommendation ||
				Object.keys(preceptorRecommendation).length === 0
			) {
				setPreceptorRecommendation(fetchedApplication.preceptorRecommendation);
			}

			setPreceptorReference(fetchedApplication.preceptorReference);
			setApplication(fetchedApplication);
		} catch (e) {
			// Do Nothing
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [callFetchApplication]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [i18n.language]);

	const fetchPreceptorByAbhyasiId = useCallback(
		async (abhyasiId: string) => {
			try {
				const response = await callApi(
					() => searchPreceptorByAbhyasiId({ value: abhyasiId }),
					'errorOccurredWhileFetchingPreceptorDetails'
				);
				return response;
			} catch (e) {
				/* empty */
			}
		},
		[callApi]
	);

	const updatePreceptorDetails = useCallback(
		async (preceptorDetails: ApplicantReference) => {
			try {
				const request: UpdatePreceptorReferenceRequest = {
					functionarySrcmId: srcmId as string,
					applicationId,
					preceptorReference: preceptorDetails,
				};
				const response = await callApi(
					() => updatePreceptorReference(request),
					'errorOccurredWhileUpdatingPreceptorDetails'
				);
				if (response) {
					void fetchApplication();
				}
				return response;
			} catch (e) {
				/* empty */
			}
		},
		[applicationId, callApi, fetchApplication, srcmId]
	);

	const value = useMemo<PreceptorInterviewContextType>(() => {
		return {
			inspirationAndWillingness,
			setInspirationAndWillingness,
			practice,
			setPractice,
			characterAndHabits,
			setCharacterAndHabits,
			availabilityAfterProgram,
			setAvailabilityAfterProgram,
			support,
			setSupport,
			understandingOfHeartfulness,
			setUnderstandingOfHeartfulness,
			dietaryHabitsAndHealth,
			setDietaryHabitsAndHealth,
			application,
			preceptorRecommendation,
			setPreceptorRecommendation,
			preceptorReference,
			setPreceptorReference,
			onSubmitClick,
			onCancelClick,
			fetchPreceptorByAbhyasiId,
			updatePreceptorDetails,
		};
	}, [
		inspirationAndWillingness,
		practice,
		characterAndHabits,
		availabilityAfterProgram,
		support,
		understandingOfHeartfulness,
		dietaryHabitsAndHealth,
		application,
		preceptorRecommendation,
		preceptorReference,
		onSubmitClick,
		onCancelClick,
		fetchPreceptorByAbhyasiId,
		updatePreceptorDetails,
	]);

	return (
		<PreceptorInterviewContext.Provider value={value}>
			{selectedRole === UserRole.SNP_ADMIN && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('adminsAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{application?.isApprover && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('approversAreNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{selectedRole === UserRole.ZC && (
				<Alert
					message={
						<div className="text-center w-full">
							<ExclamationCircleFilled className="text-warning" />{' '}
							{t('zcIsNotAllowed')}
						</div>
					}
					type="warning"
				/>
			)}
			{children}
		</PreceptorInterviewContext.Provider>
	);
};

export const usePreceptorInterview = () => {
	return useContext(PreceptorInterviewContext);
};
